import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { supabase } from './lib/supabaseClient';
import { staticContactItems, staticWorkItems } from './staticPortfolioData';

const quickLinks = [
  { id: 'interests', label: 'My Interests' },
  { id: 'work', label: 'My Work' },
  { id: 'contact', label: 'Contact Me' },
];

const publicAsset = path => `${process.env.PUBLIC_URL}${path}`;
const siteIcon = publicAsset('/favicon.ico');

const contactIconAssets = [
  { terms: ['discord'], iconUrl: publicAsset('/discord.png') },
  { terms: ['gmail', 'email', 'mail'], iconUrl: publicAsset('/gmail.png') },
  { terms: ['whatsapp', 'whats app', 'phone', 'call'], iconUrl: publicAsset('/whatsapp.png') },
];

const resolveIconUrl = iconUrl => {
  if (!iconUrl) return '';
  const trimmed = `${iconUrl}`.trim();
  if (!trimmed) return '';
  if (/^(data:|blob:|https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return publicAsset(trimmed);
  return publicAsset(`/${trimmed}`);
};

const getContactIconUrl = item => {
  const explicitIconUrl = resolveIconUrl(item?.icon_url || item?.iconUrl);
  if (explicitIconUrl) return explicitIconUrl;

  const searchable = [
    item?.contact_type,
    item?.title,
    item?.description,
    item?.redirect_link,
    item?.redirectLink,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    contactIconAssets.find(asset => asset.terms.some(term => searchable.includes(term)))
      ?.iconUrl || siteIcon
  );
};

const IconSvg = ({ name }) => {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    focusable: 'false',
    'aria-hidden': 'true',
  };

  switch (name) {
    case 'car':
      return (
        <svg {...commonProps}>
          <path className="car-gauge-arc" d="M5 16a7 7 0 0 1 14 0" />
          <path className="car-gauge-ticks" d="M7.1 12.5 5.7 11M12 9V6.8M16.9 12.5l1.4-1.5" />
          <path className="car-gauge-needle" d="M12 16 12 9.2" />
          <circle className="car-gauge-hub" cx="12" cy="16" r="1.35" />
          <path className="car-gauge-base" d="M4.8 18h14.4" />
        </svg>
      );
    case 'layout':
      return (
        <svg {...commonProps}>
          <rect x="4" y="5" width="16" height="14" rx="2.5" />
          <path d="M4 10h16M10 10v9" />
        </svg>
      );
    case 'ai':
      return (
        <svg {...commonProps}>
          <rect x="6" y="6" width="12" height="12" rx="3" />
          <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
          <path d="M9.5 13.5c1.5 1.1 3.5 1.1 5 0" />
        </svg>
      );
    case 'code':
      return (
        <svg {...commonProps}>
          <path d="m9 8-4 4 4 4M15 8l4 4-4 4M13 5l-2 14" />
        </svg>
      );
    case 'tennis':
      return (
        <svg {...commonProps}>
          <circle className="tennis-ball-core" cx="12" cy="12" r="6.6" />
          <path className="tennis-ball-seam" d="M7.2 7.8c3 1.7 3.5 6.8.9 8.8M16.8 7.8c-3 1.7-3.5 6.8-.9 8.8" />
        </svg>
      );
    case 'book':
      return (
        <svg {...commonProps}>
          <path className="book-cover-left" d="M12 6.2C9.9 4.9 7.6 4.4 5 4.8v12.7c2.6-.4 4.9.1 7 1.5V6.2Z" />
          <path className="book-cover-right" d="M12 6.2c2.1-1.3 4.4-1.8 7-1.4v12.7c-2.6-.4-4.9.1-7 1.5V6.2Z" />
          <path className="book-spine" d="M12 6.2V19" />
          <path className="book-lines" d="M7.1 8.2c1.1 0 2 .2 2.9.6M16.9 8.2c-1.1 0-2 .2-2.9.6" />
        </svg>
      );
    case 'cooking':
      return (
        <svg {...commonProps}>
          <path d="M6 10h12v4a6 6 0 0 1-12 0v-4Z" />
          <path d="M4 10h16M8 6c0-1.2 1-2 2-2M12 6c0-1.2 1-2 2-2M16 6c0-1.2 1-2 2-2" />
        </svg>
      );
    case 'study':
      return (
        <svg {...commonProps}>
          <path className="study-cap" d="m3 8 9-4 9 4-9 4-9-4Z" />
          <path className="study-band" d="M7 10.2V15c2.7 2 7.3 2 10 0v-4.8M20 9v5" />
        </svg>
      );
    default:
      return (
        <svg {...commonProps}>
          <path d="M12 3 14.2 9.8H21l-5.5 4 2.1 6.7-5.6-4.1-5.6 4.1 2.1-6.7-5.5-4h6.8L12 3Z" />
        </svg>
      );
  }
};

const interestItems = [
{
  title: 'Cars',
  icon: 'car',
  blurb: 'I closely follow the Indian car market and track new launches, designs, specifications, prices, and comparisons.',
  stack: 'Design + Specs',
},
{
  title: 'UI/UX',
  icon: 'layout',
  blurb: 'I design clean, easy to use interfaces and enjoy experimenting with layouts, spacing, and small interactions.',
  stack: 'Layouts + Motion',
},
{
  title: 'AI',
  icon: 'ai',
  blurb: 'I work with AI APIs and build useful AI driven websites while learning how these systems work.',
  stack: 'LLMs + Agents',
},
{
  title: 'Code',
  icon: 'code',
  blurb: 'I turn ideas into real projects and use Codex regularly to build and experiment faster.',
  stack: 'React + Codex',
},
{
  title: 'Tennis',
  icon: 'tennis',
  blurb: 'I train daily with two hours of tennis and about forty five minutes of fitness to stay disciplined and improve.',
  stack: 'Improvement + Fitness',
},
{
  title: 'Reading',
  icon: 'book',
  blurb: 'I enjoy reading to learn new ideas, build focus, and explore different stories and perspectives.',
  stack: 'Books + Focus',
},
{
  title: 'Baking/Cooking',
  icon: 'cooking',
  blurb: 'I like experimenting with recipes, flavors, and techniques in the kitchen.',
  stack: 'Recipes + Creativity',
},
{
  title: 'NIOS',
  icon: 'study',
  blurb: 'I’m homeschooled and studying for my NIOS Class 10, even though I’m a year younger.',
  stack: 'Self paced + Discipline',
},
];

const WORK_SELECT = '*';

const CONTACT_SELECT = 'id,contact_type,description,redirect_link,icon_url,used_times';

const defaultContactItems = [
  ...staticContactItems.map(item => ({
    ...item,
    iconUrl: getContactIconUrl(item),
  })),
];

const normalizeExternalUrl = url => {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
};

const formatDataError = (error, fallbackMessage) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You appear to be offline. Please check your connection and try again.';
  }

  const message = `${error?.message || ''}`.toLowerCase();
  if (message.includes('failed to fetch') || message.includes('networkerror')) {
    return 'We could not reach the server. Please check your connection and try again.';
  }
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'That request took too long. Please try again in a moment.';
  }
  if (
    message.includes('permission') ||
    message.includes('not authorized') ||
    message.includes('not authorised')
  ) {
    return 'Access was denied. Please try again later.';
  }
  if (message.includes('database') || message.includes('postgres') || message.includes('connection')) {
    return 'We are having trouble reaching the database. Please try again shortly.';
  }

  return fallbackMessage;
};

const renderWorkDescription = description => {
  if (!description) return null;
  const parts = [];
  const pattern = /\{([^:}]+):([^}]+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(description)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: description.slice(lastIndex, match.index) });
    }
    const label = match[1].trim();
    const url = match[2].trim();
    if (label && url) {
      parts.push({ type: 'link', label, url });
    } else {
      parts.push({ type: 'text', value: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < description.length) {
    parts.push({ type: 'text', value: description.slice(lastIndex) });
  }

  return parts.map((part, index) => {
    if (part.type === 'link') {
      return (
        <a
          key={`${part.url}-${index}`}
          className="detail-inline-link"
          href={part.url}
          target="_blank"
          rel="noreferrer"
        >
          {part.label}
        </a>
      );
    }
    return <span key={`text-${index}`}>{part.value}</span>;
  });
};

const getPlainWorkDescription = description =>
  (description || '').replace(/\{([^:}]+):([^}]+)\}/g, '$1');

const ChakraSVG = () => (
  <svg viewBox="0 0 20 20" width="10" height="10" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7.2" fill="none" stroke="#000080" strokeWidth="1.6" />
    <line x1="10" y1="4.2" x2="10" y2="15.8" stroke="#000080" strokeWidth="1.1" />
    <line x1="4.2" y1="10" x2="15.8" y2="10" stroke="#000080" strokeWidth="1.1" />
    <line x1="5.9" y1="5.9" x2="14.1" y2="14.1" stroke="#000080" strokeWidth="1.1" />
    <line x1="14.1" y1="5.9" x2="5.9" y2="14.1" stroke="#000080" strokeWidth="1.1" />
  </svg>
);

function App() {
  const [workItems, setWorkItems] = useState(staticWorkItems);
  const [contactItems, setContactItems] = useState(defaultContactItems);
  const [workError, setWorkError] = useState(null);
  const [contactError, setContactError] = useState(null);
  const [workLoading, setWorkLoading] = useState(Boolean(supabase));
  const [selectedWork, setSelectedWork] = useState(null);
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0, scale: 0.5 });
  const [interestMotion, setInterestMotion] = useState({});
  const detailCardRef = useRef(null);
  const revealObserverRef = useRef(null);
  const interestMotionTimeouts = useRef({});
  const racketImage = `${process.env.PUBLIC_URL}/tennis-racket.svg`;
  const carOneImage = `${process.env.PUBLIC_URL}/car-1.svg`;
  const carTwoImage = `${process.env.PUBLIC_URL}/car-2.svg`;
  const observeRevealTargets = useCallback(() => {
    const observer = revealObserverRef.current;
    if (!observer) return;
    const targets = document.querySelectorAll('.reveal');
    targets.forEach(el => {
      if (el.dataset.revealObserved === 'true') return;
      el.dataset.revealObserved = 'true';
      observer.observe(el);
    });
  }, []);

  const handleInterestEnter = useCallback(title => {
    const timeoutId = interestMotionTimeouts.current[title];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete interestMotionTimeouts.current[title];
    }
    setInterestMotion(current => ({ ...current, [title]: 'hovered' }));
  }, []);

  const handleInterestLeave = useCallback(title => {
    const timeoutId = interestMotionTimeouts.current[title];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
    setInterestMotion(current => ({ ...current, [title]: 'exiting' }));
    interestMotionTimeouts.current[title] = window.setTimeout(() => {
      setInterestMotion(current => {
        const next = { ...current };
        delete next[title];
        return next;
      });
      delete interestMotionTimeouts.current[title];
    }, 720);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadWork = async () => {
      setWorkLoading(true);
      setWorkError(null);
      if (!supabase) {
        setWorkLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('my_work')
          .select(WORK_SELECT)
          .order('year_started', { ascending: false });

        if (isCancelled) return;
        if (error) {
          setWorkError(
            formatDataError(error, 'Unable to load work items right now. Please try again.')
          );
          return;
        }

        const mapped = (data || []).map(item => ({
          id: item.id,
          title: item.name,
          year: item.year_started ? String(item.year_started) : '',
          stack: item.structure || '',
          summary: item.card_description || '',
          description: item.dialog_description || '',
          links: item.link
            ? [
                {
                  label: item.button_text?.trim() || 'Visit Site',
                  url: normalizeExternalUrl(item.link),
                },
              ]
            : [],
        }));
        setWorkItems(mapped);
      } catch (err) {
        if (!isCancelled) {
          setWorkError(
            formatDataError(err, 'Unable to load work items right now. Please try again.')
          );
        }
      } finally {
        if (!isCancelled) {
          setWorkLoading(false);
        }
      }
    };

    const loadContact = async () => {
      setContactError(null);
      if (!supabase) {
        setContactItems(defaultContactItems);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('contact_me')
          .select(CONTACT_SELECT)
          .order('used_times', { ascending: false, nullsLast: true })
          .order('id', { ascending: true });

        if (isCancelled) return;
        if (error) {
          setContactError(
            formatDataError(error, 'Unable to load contact options right now. Please try again.')
          );
          setContactItems(defaultContactItems);
          return;
        }

        const mapped =
          data && data.length > 0
            ? data.map(item => {
                const parsedUsedTimes = Number(item.used_times);
                return {
                  id: item.id,
                  title: item.contact_type,
                  description: item.description,
                  redirectLink: normalizeExternalUrl(item.redirect_link),
                  iconUrl: getContactIconUrl(item),
                  usedTimes: Number.isFinite(parsedUsedTimes) ? parsedUsedTimes : 0,
                };
              })
            : defaultContactItems;
        setContactItems(mapped);
      } catch (err) {
        if (!isCancelled) {
          setContactError(
            formatDataError(err, 'Unable to load contact options right now. Please try again.')
          );
          setContactItems(defaultContactItems);
        }
      }
    };

    loadWork();
    loadContact();

    return () => {
      isCancelled = true;
    };
  }, []);

  const trackContactClick = useCallback(async contactId => {
    if (!supabase || `${contactId}`.startsWith('static-')) return;

    try {
      const { data, error } = await supabase
        .from('contact_me')
        .select('used_times')
        .eq('id', contactId)
        .single();

      if (error) return;
      const current = Number(data?.used_times);
      const safeCurrent = Number.isFinite(current) ? current : 0;

      const { error: updateError } = await supabase
        .from('contact_me')
        .update({ used_times: safeCurrent + 1 })
        .eq('id', contactId);
      if (updateError) return;

      setContactItems(items =>
        items.map(item =>
          item.id === contactId
            ? { ...item, usedTimes: (Number.isFinite(item.usedTimes) ? item.usedTimes : 0) + 1 }
            : item
        )
      );
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );
    revealObserverRef.current = observer;
    observeRevealTargets();

    return () => {
      observer.disconnect();
      revealObserverRef.current = null;
    };
  }, [observeRevealTargets]);

  useEffect(() => {
    observeRevealTargets();
  }, [observeRevealTargets, workItems, contactItems]);

  const usedTimesValues = contactItems.map(item =>
    Number.isFinite(item.usedTimes) ? item.usedTimes : 0
  );
  const maxUsedContact = usedTimesValues.length ? Math.max(...usedTimesValues) : null;
  const minUsedContact = usedTimesValues.length ? Math.min(...usedTimesValues) : null;
  const maxUsedContactCount =
    maxUsedContact === null
      ? 0
      : usedTimesValues.filter(value => value === maxUsedContact).length;
  const showUsageBadge =
    maxUsedContact !== null &&
    minUsedContact !== null &&
    maxUsedContact > 0 &&
    maxUsedContact !== minUsedContact;

  const scrollTo = id => {
    const el = document.getElementById(id);
    if (el) {
      const header = document.querySelector('.top-bar');
      const headerOffset = header ? header.getBoundingClientRect().height + 24 : 0;
      const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        document.documentElement.style.setProperty('--scrollY', y.toString());
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!selectedWork) return undefined;
    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusables = detailCardRef.current?.querySelectorAll(focusableSelectors) || [];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    (first || detailCardRef.current)?.focus();

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSelectedWork(null);
      }
      if (event.key === 'Tab' && focusables.length > 0) {
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedWork]);

  useEffect(() => {
    const timeouts = interestMotionTimeouts.current;
    return () => {
      Object.values(timeouts).forEach(window.clearTimeout);
    };
  }, []);

  return (
    <div className="app-shell">
      <div className="grid-bg" aria-hidden="true" />
      <div className="glow" aria-hidden="true" />
      <div
        className="floating-racket rack-1"
        aria-hidden="true"
        style={{ backgroundImage: `url(${racketImage})` }}
      />
      <div
        className="floating-racket rack-2"
        aria-hidden="true"
        style={{ backgroundImage: `url(${racketImage})` }}
      />
      <div
        className="floating-car car-1"
        aria-hidden="true"
        style={{ backgroundImage: `url(${carOneImage})` }}
      />
      <div
        className="floating-car car-2"
        aria-hidden="true"
        style={{ backgroundImage: `url(${carTwoImage})` }}
      />
      <div className="floating-terminal" aria-hidden="true" />
      <header className="top-bar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <img src={siteIcon} alt="" />
          </span>
          <div>
            <p className="tiny">Who I Am</p>
            <h1>Aarav Kumta</h1>
          </div>
        </div>
        <nav className="quick-nav">
          {quickLinks.map(link => (
            <button
              key={link.id}
              onClick={() => {
                scrollTo(link.id);
              }}
              className="ghost"
            >
              {link.label}
            </button>
          ))}
        </nav>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="hero-text">
            <div className="pill">Cars • UI • AI • Code • Tennis</div>
            <h2>Building modern digital experiences at 15.</h2>
            <p className="lede">
              Hey, I’m Aarav. I’m 15 and I enjoy working with code and design, especially building interfaces and trying out new ideas. I’m currently homeschooled and studying for my NIOS Class 10.
            </p>
            <div className="cta-row">
              <button
                className="primary"
                onClick={() => {
                  scrollTo('work');
                }}
              >
                View my work
              </button>
              <button
                className="secondary"
                onClick={() => {
                  scrollTo('contact');
                }}
              >
                Get in touch
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="orbital">
              <div className="orbit" />
              <div className="pulse" />
            </div>
          </div>
        </section>

        <section className="panel" id="interests">
          <div className="panel-header">
            <p className="tiny">01</p>
            <h3>My Interests</h3>
            <p>The things I enjoy and spend time learning.</p>
          </div>
          <div className="tile-grid">
            {interestItems.map(item => {
              const motionClass =
                interestMotion[item.title] === 'hovered'
                  ? 'is-interest-hovered'
                  : interestMotion[item.title] === 'exiting'
                    ? 'is-interest-exiting'
                    : '';

              return (
                <div
                  key={item.title}
                  className={`tile interest-tile tennis-animate ${motionClass}`.trim()}
                  aria-hidden="false"
                  onMouseEnter={() => handleInterestEnter(item.title)}
                  onMouseLeave={() => handleInterestLeave(item.title)}
                  onFocus={() => handleInterestEnter(item.title)}
                  onBlur={() => handleInterestLeave(item.title)}
                >
                  <div className="tile-top">
                    <h4>
                      {item.title}
                      <span className={`interest-icon interest-icon-${item.icon}`} aria-hidden="true">
                        <IconSvg name={item.icon} />
                      </span>
                    </h4>
                  </div>
                  <p>{item.blurb}</p>
                  <span className="pill tiny highlight">{item.stack}</span>
                  <div className="glint" />
                </div>
              );
            })}
          </div>
        </section>

        <section className="panel" id="work">
          <div className="panel-header">
            <p className="tiny">02</p>
            <h3>My Work</h3>
            <p>A glimpse of all the projects and experiments I have made.</p>
          </div>
          <div className="work-list">
            {workLoading && (
              <div className="work-loading" role="status" aria-live="polite">
                <span className="work-loading-spinner" aria-hidden="true" />
                <span>Loading items...</span>
              </div>
            )}
            {workLoading &&
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`work-skeleton-${index}`}
                  className="work-card work-card--skeleton"
                  aria-hidden="true"
                >
                  <div className="work-body">
                    <div className="work-skeleton-line work-skeleton-title" />
                    <div className="work-skeleton-line" />
                    <div className="work-skeleton-line short" />
                    <div className="work-skeleton-pill" />
                  </div>
                </div>
              ))}
            {!workLoading && workError && (
              <p className="muted">Could not load work items: {workError}</p>
            )}
            {!workLoading && !workError && workItems.length === 0 && (
              <p className="muted">No work items yet.</p>
            )}
            {workItems.map(item => (
              <button
                key={item.id}
                className="work-card"
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const centerX = rect.left + rect.width / 2;
                  const centerY = rect.top + rect.height / 2;
                  const targetWidth = Math.min(900, window.innerWidth * 0.96);
                  const scale = Math.max(0.25, Math.min(rect.width / targetWidth, 1));
                  setModalOffset({
                    x: centerX - window.innerWidth / 2,
                    y: centerY - window.innerHeight / 2,
                    scale,
                  });
                  setSelectedWork(item);
                }}
                aria-label={`Open ${item.title} details`}
              >
                <div className="work-body">
                  <div className="work-header">
                    <h4>{item.title}</h4>
                    <span className="arrow">↗</span>
                  </div>
                  <p className="work-summary">
                    {item.summary || getPlainWorkDescription(item.description)}
                  </p>
                  <div className="work-meta">
                    <span className="pill small">{item.stack}</span>
                    <span className="pill small started-pill">Started {item.year}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="panel contact" id="contact">
          <div className="panel-header">
            <p className="tiny">03</p>
            <h3>Contact Me</h3>
            <p>Open to internships, collaborations or focused discussions.</p>
          </div>
          <div className="contact-card">
            <div className="contact-actions">
              {contactError && (
                <p className="muted">Could not load contact options: {contactError}</p>
              )}
              {contactItems.map(item => (
                <a
                  key={item.id}
                  className={`contact-cta${
                    showUsageBadge && item.usedTimes === maxUsedContact
                      ? ' is-most-used'
                      : ''
                  }`}
                  href={item.redirectLink || '#'}
                  onClick={event => {
                    trackContactClick(item.id);

                    const isModifiedClick =
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey ||
                      event.button === 1;
                    if (isModifiedClick) return;

                    if (!item.redirectLink) {
                      event.preventDefault();
                      return;
                    }

                    if (item.redirectLink.startsWith('http')) {
                      event.preventDefault();
                      window.open(item.redirectLink, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  {showUsageBadge && item.usedTimes === maxUsedContact && (
                    <span className="most-used-pill">
                      {maxUsedContactCount > 1 ? 'Frequently used' : 'Most used'}
                    </span>
                  )}
                  <span className="cta-icon" aria-hidden="true">
                    <img className="cta-icon-img" src={item.iconUrl || siteIcon} alt="" />
                  </span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      {selectedWork && (
        <div
          className="work-detail"
          role="dialog"
          aria-modal="true"
          aria-labelledby="work-detail-title"
          aria-describedby="work-detail-description"
          onClick={event => {
            if (event.target === event.currentTarget) {
              setSelectedWork(null);
            }
          }}
        >
          <div
            className="detail-card modal-animate"
            tabIndex={-1}
            ref={detailCardRef}
            style={{
              '--modal-offset-x': `${modalOffset.x}px`,
              '--modal-offset-y': `${modalOffset.y}px`,
              '--modal-scale': modalOffset.scale,
            }}
          >
            <div className="detail-header">
              <div>
                <p className="tiny">{selectedWork.year}</p>
                <h3 id="work-detail-title">{selectedWork.title}</h3>
                <span className="pill small detail-stack">{selectedWork.stack}</span>
              </div>
              <button
                className="ghost"
                onClick={() => {
                  setSelectedWork(null);
                }}
              >
                Close
              </button>
            </div>
            <p className="detail-copy" id="work-detail-description">
              {renderWorkDescription(selectedWork.description)}
            </p>
            <div className="detail-links">
              {selectedWork.links.map(link => (
                <a
                  key={link.url}
                  className="primary link-btn"
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="footer-content">
          <span className="flag-icon" aria-hidden="true">
            <ChakraSVG />
          </span>
          <p>Made with pride in India</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
